import React, { useState, useMemo } from "react";
import { Chip } from "./DispatchModal";
import { resolveName, nameKey } from "../utils/names";

/**
 * GuardMulti — multi-select with autocomplete.
 * Props:
 *   guards: string[]         — currently selected guards
 *   onChange: (string[]) => void
 *   known: string[]          — autocomplete source (loaded from Firestore)
 *   facilityId: string       — to persist new guards
 *   aliases: object          — facility.nameAliases.guards (variante → canónico)
 */
export default function GuardMulti({ guards, onChange, known, facilityId, aliases }) {
  const [input, setInput]     = useState("");
  const [focused, setFocused] = useState(false);

  const gArr = Array.isArray(guards) ? guards : [];
  const kArr = Array.isArray(known) ? known : [];

  const suggestions = useMemo(() => {
    const lower = input.toLowerCase();
    return kArr
      .filter(g => !gArr.includes(g) && (input.length < 1 || g.toLowerCase().includes(lower)))
      .slice(0, 6);
  }, [input, kArr, gArr]);

  const add = (name) => {
    // Normaliza (Title Case), aplica aliases del recinto y reutiliza la
    // variante ya conocida (match sin tildes/mayúsculas) para no crear duplicados.
    const resolved = resolveName(name, aliases, kArr);
    const key = nameKey(resolved);
    if (!key || gArr.some(g => nameKey(g) === key)) {
      setInput("");
      return;
    }
    onChange([...gArr, resolved]);
    setInput("");
  };

  const [showMore, setShowMore] = useState(false);
  const allChips = Array.from(new Set([...kArr, ...gArr]));

  const toggle = (name) => {
    if (gArr.includes(name)) {
      onChange(gArr.filter(x => x !== name));
    } else {
      onChange([...gArr, name]);
    }
  };

  return (
    <div>
      {/* Quick selection chips */}
      {allChips.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 8 }}>
          {allChips.slice(0, 4).map(g => (
            <Chip key={g} sel={guards.includes(g)} onClick={() => toggle(g)} color="#22c55e">{g}</Chip>
          ))}
          {allChips.length > 4 && (
            !showMore ? (
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); setShowMore(true); }}
                style={{
                  padding: "6px 12px", fontSize: 10, fontWeight: 700,
                  background: "transparent", color: "var(--bright)",
                  border: "1px dashed #22c55e40", borderRadius: 6,
                  cursor: "pointer", transition: "all 0.2s"
                }}
              >
                + Más...
              </button>
            ) : (
              allChips.slice(4).map(g => (
                <Chip key={g} sel={guards.includes(g)} onClick={() => toggle(g)} color="#22c55e">{g}</Chip>
              ))
            )
          )}
        </div>
      )}

      {/* Input + dropdown */}
      <div style={{ position: "relative" }}>
        <input
          className="v3-input"
          value={input}
          onChange={e => setInput(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => {
            // Auto-add typed text when user clicks away (e.g. to submit)
            if (input.trim() && !gArr.includes(input.trim())) {
              add(input);
            }
            setTimeout(() => setFocused(false), 200);
          }}
          onKeyDown={e => {
            if (e.key === "Enter") { e.preventDefault(); if (input.trim()) add(input); }
          }}
          placeholder={gArr.length ? "Agregar otro guardia..." : "Nombre del guardia..."}
          style={{ borderColor: focused ? "#3b82f6" : "var(--border)" }}
        />
        {focused && suggestions.length > 0 && (
          <div style={{
            position: "absolute", top: "100%", left: 0, right: 0, marginTop: 3,
            background: "var(--surface)", border: "1px solid var(--border)",
            borderRadius: 7, overflow: "hidden", zIndex: 50,
            maxHeight: 180, overflowY: "auto",
          }}>
            {suggestions.map(g => (
              <button
                key={g} type="button"
                onMouseDown={() => add(g)}
                style={{
                  width: "100%", padding: "9px 10px", border: "none",
                  background: "transparent", color: "var(--text)",
                  fontSize: 12, textAlign: "left", cursor: "pointer",
                  borderBottom: "1px solid var(--border)",
                }}
              >{g}</button>
            ))}
            {input.trim() && !kArr.includes(input.trim()) && !gArr.includes(input.trim()) && (
              <div style={{ padding: "6px 10px", fontSize: 10, color: "var(--muted)" }}>
                ↵ Enter para agregar "{input.trim()}"
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
